import type { MediaAsset } from '@ghostscreen/contracts';
import { i18n } from '../../i18n/i18n';

export type MediaOperatorStatus = {
  label: string;
  tone: string;
  hint: string | null;
  /** True while the operator should wait on TV optimize / prepare. */
  inProgress: boolean;
  /** Show Retry optimize when conversion is not actually running. */
  canRetryOptimize: boolean;
};

/**
 * Operator-facing library status. Videos are not "Ready to use" until
 * playbackReady (H.264 variant) exists — even if DB status is ready.
 */
export function getMediaOperatorStatus(asset: MediaAsset): MediaOperatorStatus {
  if (asset.status === 'pending') {
    return {
      label: i18n.t('content:status.pending.label'),
      tone: 'text-[var(--gs-warn)]',
      hint: i18n.t('content:status.pending.hint'),
      inProgress: false,
      canRetryOptimize: false,
    };
  }

  if (asset.status === 'failed') {
    return {
      label: i18n.t('content:status.failed.label'),
      tone: 'text-bad',
      hint: i18n.t('content:status.failed.hint'),
      inProgress: false,
      canRetryOptimize: asset.kind === 'video',
    };
  }

  if (asset.kind === 'video' && asset.playbackReady !== true) {
    // Only treat as "in flight" when a real job is queued — status=processing
    // alone can mean a dead job, which must still show Retry.
    if (asset.optimizeQueued === true) {
      return {
        label: i18n.t('content:status.optimizing.label'),
        tone: 'text-[var(--gs-warn)]',
        hint: i18n.t('content:status.optimizing.hint'),
        inProgress: true,
        canRetryOptimize: false,
      };
    }
    return {
      label: i18n.t('content:status.waiting.label'),
      tone: 'text-[var(--gs-warn)]',
      hint: i18n.t('content:status.waiting.hint'),
      inProgress: true,
      canRetryOptimize: true,
    };
  }

  if (asset.status === 'processing') {
    return {
      label: i18n.t('content:status.preparing.label'),
      tone: 'text-[var(--gs-warn)]',
      hint: i18n.t('content:status.preparing.hint'),
      inProgress: true,
      canRetryOptimize: false,
    };
  }

  return {
    label:
      asset.kind === 'video' ? i18n.t('content:status.readyForTv') : i18n.t('content:status.ready'),
    tone: 'text-ok',
    hint: null,
    inProgress: false,
    canRetryOptimize: false,
  };
}

export function isMediaUsableInPlaylist(asset: MediaAsset): boolean {
  if (asset.status !== 'ready') return false;
  if (asset.kind === 'video') return asset.playbackReady === true;
  return true;
}
